import { useState } from 'react';
import { Star, Award, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BookingDrawer } from '../components/BookingDrawer';
import { COACHES, CLASSES } from '../data/mockData';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export const Coaches = () => {
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleBookWithCoach = (coach) => {
    // Find next available class with this coach
    const coachClasses = CLASSES.filter(c => c.coachId === coach.id && c.status !== 'full');
    if (coachClasses.length > 0) {
      setSelectedClass(coachClasses[0]);
      setIsDrawerOpen(true);
    }
  };

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-24 md:pb-8 bg-slate-50" data-testid="coaches-page">
      <div className="container-app">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4 block">
            Nuestro equipo
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">
            Coaches expertos
          </h1>
          <p className="text-lg text-slate-500 max-w-xl mx-auto">
            Profesionales certificados listos para guiarte en tu transformación.
          </p>
        </div>

        {/* Coaches Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {COACHES.map((coach, index) => (
            <div
              key={coach.id}
              className={`coach-card cursor-pointer animate-fade-in-up stagger-${index + 1}`}
              onClick={() => setSelectedCoach(coach)}
              data-testid={`coach-card-${coach.id}`}
            >
              <img
                src={coach.image}
                alt={coach.name}
                className="absolute inset-0 w-full h-full object-cover img-zoom"
              />
              <div className="coach-card-overlay" />
              
              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <div className="flex items-center gap-1 mb-2">
                  <Star className="w-4 h-4 fill-[#16263B] text-[#16263B]" />
                  <span className="text-sm font-medium">{coach.rating}</span>
                  <span className="text-sm text-white/60">({coach.reviews})</span>
                </div>
                <h3 className="font-heading font-bold text-xl mb-1">{coach.name}</h3>
                <p className="text-sm text-white/80">{coach.specialty}</p>
              </div>

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-[#16263B] opacity-0 group-hover:opacity-10 transition-opacity" />
            </div>
          ))}
        </div>
      </div>

      {/* Coach Detail Modal */}
      <Dialog open={!!selectedCoach} onOpenChange={(open) => !open && setSelectedCoach(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          {selectedCoach && (
            <>
              {/* Coach Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={selectedCoach.image}
                  alt={selectedCoach.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="flex items-center gap-1 mb-2">
                    <Star className="w-4 h-4 fill-[#16263B] text-[#16263B]" />
                    <span className="font-medium">{selectedCoach.rating}</span>
                    <span className="text-white/60">({selectedCoach.reviews} reseñas)</span>
                  </div>
                  <h2 className="font-heading font-bold text-2xl">{selectedCoach.name}</h2>
                  <p className="text-white/80">{selectedCoach.specialty}</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-slate-600 mb-6">{selectedCoach.bio}</p>

                {/* Certifications */}
                <div className="mb-6">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-3">
                    <Award className="w-4 h-4 text-[#16263B]" />
                    Certificaciones
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCoach.certifications.map((cert, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-600"
                      >
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Classes */}
                <div className="mb-6">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-3">
                    <Calendar className="w-4 h-4 text-[#16263B]" />
                    Clases que imparte
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCoach.classes.map((cls, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-[#16263B]/20 rounded-full text-sm font-medium text-slate-900"
                      >
                        {cls}
                      </span>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={() => handleBookWithCoach(selectedCoach)}
                  className="w-full btn-primary h-12"
                  data-testid={`book-with-coach-${selectedCoach.id}`}
                >
                  Reservar con {selectedCoach.name.split(' ')[0]}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Booking Drawer */}
      <BookingDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        classData={selectedClass}
      />
    </div>
  );
};
